import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile, formatDateRanges } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const LocationNameTrendsTable = ({ selectedLocation, selectedCrime, selectedDateRange, selectedSource, compareMode }) => {
  const current = selectedLocation?.data?.current;
  const locationType = selectedLocation.info?.type?.charAt(0).toUpperCase() + selectedLocation.info?.type?.slice(1);
  const isNation = selectedLocation.info?.type?.toLowerCase() === 'nation';
  const isCounty = selectedLocation.info?.type?.toLowerCase() === 'county';
  const isCity = selectedLocation.info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  if (selectedLocation?.loading || !selectedLocation?.data?.current || selectedLocation?.info?.name?.startsWith('All ')) {
    return null;
  }

  const hasCrimeData = [
    'all_crime_count', 'violent_crime_count', 'property_crime_count',
    'murder_count', 'rape_count', 'robbery_count', 'aggravated_assault_count',
    'burglary_count', 'larceny_theft_count', 'motor_vehicle_theft_count'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );
  
  if (!hasCrimeData) {
    return <div className="loading">No Data Available</div>;
  }

  const hasPreviousCrimeData = [
    'all_crime_count_prev', 'violent_crime_count_prev', 'property_crime_count_prev',
    'murder_count_prev', 'rape_count_prev', 'robbery_count_prev', 'aggravated_assault_count_prev',
    'burglary_count_prev', 'larceny_theft_count_prev', 'motor_vehicle_theft_count_prev'
  ].some(field => 
    current[field] !== null && 
    current[field] !== undefined && 
    current[field] !== 'N/A'
  );

  if (!hasPreviousCrimeData) {
    return (
        <div className="loading">No Previous Data Available</div>
    );
  }

  const transformData = () => {
    const isNCVS = selectedSource === 'BJS_NCVS';
  
    const getComparisonFields = (prefix) => {
      const stateRankField = `${prefix}_state_trend_rank`;
      const nationalRankField = `${prefix}_national_trend_rank`;
      
      const rankField = isCountyOrCity
        ? compareMode === 'state'
          ? stateRankField
          : nationalRankField
        : `${prefix}_trend_rank`;
      
      const statePercentileField = `${prefix}_state_trend_percentile`;
      const nationalPercentileField = `${prefix}_national_trend_percentile`;
      
      const percentileField = isCountyOrCity
        ? compareMode === 'state'
          ? statePercentileField
          : nationalPercentileField
        : `${prefix}_trend_percentile`;
      
      return {
        rank: current[rankField],
        percentile: current[percentileField]
      };
    };
  
    const allCrimes = [
      {
        crimeType: 'All Crime',
        level: 0,
        currentTotal: current.all_crime_count,
        previousTotal: current.all_crime_count_prev,
        difference: current.all_crime_count_prev_diff,
        percentDiff: current.all_crime_count_prev_diff_pct,
        ...getComparisonFields('all_crime')
      },
      {
        crimeType: 'Violent Crime',
        level: 1,
        currentTotal: current.violent_crime_count,
        previousTotal: current.violent_crime_count_prev,
        difference: current.violent_crime_count_prev_diff,
        percentDiff: current.violent_crime_count_prev_diff_pct,
        ...getComparisonFields('violent_crime')
      },
      {
        crimeType: 'Murder',
        level: 2,
        currentTotal: current.murder_count,
        previousTotal: current.murder_count_prev,
        difference: current.murder_count_prev_diff,
        percentDiff: current.murder_count_prev_diff_pct,
        ...getComparisonFields('murder')
      },
      {
        crimeType: 'Rape',
        level: 2,
        currentTotal: current.rape_count,
        previousTotal: current.rape_count_prev,
        difference: current.rape_count_prev_diff,
        percentDiff: current.rape_count_prev_diff_pct,
        ...getComparisonFields('rape')
      },
      {
        crimeType: 'Robbery',
        level: 2,
        currentTotal: current.robbery_count,
        previousTotal: current.robbery_count_prev,
        difference: current.robbery_count_prev_diff,
        percentDiff: current.robbery_count_prev_diff_pct,
        ...getComparisonFields('robbery')
      },
      {
        crimeType: 'Aggravated Assault',
        level: 2,
        currentTotal: current.aggravated_assault_count,
        previousTotal: current.aggravated_assault_count_prev,
        difference: current.aggravated_assault_count_prev_diff,
        percentDiff: current.aggravated_assault_count_prev_diff_pct,
        ...getComparisonFields('aggravated_assault')
      },
      ...(isNCVS ? [{
        crimeType: 'Simple Assault',
        level: 2,
        currentTotal: current.simple_assault_count,
        previousTotal: current.simple_assault_count_prev,
        difference: current.simple_assault_count_prev_diff,
        percentDiff: current.simple_assault_count_prev_diff_pct,
      }] : []),
      {
        crimeType: 'Property Crime',
        level: 1,
        currentTotal: current.property_crime_count,
        previousTotal: current.property_crime_count_prev,
        difference: current.property_crime_count_prev_diff,
        percentDiff: current.property_crime_count_prev_diff_pct,
        ...getComparisonFields('property_crime')
      },
      {
        crimeType: 'Burglary',
        level: 2,
        currentTotal: current.burglary_count,
        previousTotal: current.burglary_count_prev,
        difference: current.burglary_count_prev_diff,
        percentDiff: current.burglary_count_prev_diff_pct,
        ...getComparisonFields('burglary')
      },
      ...(isNCVS ? [{
        crimeType: 'Trespassing',
        level: 2,
        currentTotal: current.trespassing_count,
        previousTotal: current.trespassing_count_prev,
        difference: current.trespassing_count_prev_diff,
        percentDiff: current.trespassing_count_prev_diff_pct,
      }] : []),
      {
        crimeType: 'Larceny/Theft',
        level: 2,
        currentTotal: current.larceny_theft_count,
        previousTotal: current.larceny_theft_count_prev,
        difference: current.larceny_theft_count_prev_diff,
        percentDiff: current.larceny_theft_count_prev_diff_pct,
        ...getComparisonFields('larceny_theft')
      },
      {
        crimeType: 'Motor Vehicle Theft',
        level: 2,
        currentTotal: current.motor_vehicle_theft_count,
        previousTotal: current.motor_vehicle_theft_count_prev,
        difference: current.motor_vehicle_theft_count_prev_diff,
        percentDiff: current.motor_vehicle_theft_count_prev_diff_pct,
        ...getComparisonFields('motor_vehicle_theft')
      }
    ];
  
    return allCrimes;
  };

  const renderRow = (item) => {
    const isSelected = item.crimeType.toLowerCase().replace(/[\s/]/g, '_') === selectedCrime?.toLowerCase();
  
    return (
      <tr key={item.crimeType} className={`level-${item.level}${isSelected ? ' selected-row' : ''}`}>
        <td className="crime-type-cell">
          <div className="crime-type-content">
            <span>{item.crimeType}</span>
          </div>
        </td>
        <td className="number-cell">{formatNumber(item.currentTotal)}</td>
        <td className="number-cell">{formatNumber(item.previousTotal)}</td>
        <td className="number-cell" style={{ color: getValueColor(item.difference) }}>
          {formatDifference(item.difference)}
        </td>
        <td className="number-cell bold" style={{ color: getValueColor(item.percentDiff) }}>
          {formatDifference(item.percentDiff, true)}
        </td>
        {!isNation && (
          <>
            <td className="rank-cell">{formatRank(item.rank)}</td>
            <td className="rank-cell">{formatPercentile(item.percentile)}</td>
          </>
        )}
      </tr>
    );
  };

  const data = transformData();

  return (
    <>
      <div className="main-table-wrapper">
        <div className="main-table-top">
          <strong>{formatDateRanges(selectedDateRange).currentRange}</strong>&nbsp;&nbsp;compared to&nbsp;&nbsp; 
          <strong>{formatDateRanges(selectedDateRange).prevRange}</strong>        
        </div>
        <div className="main-table-scroll-container location-name-table">
          <table className={`main-table location-name-table trends-table ${isNation ? 'nation-view' : ''}`}>
            <thead>
              <tr>
                <th>Type of Crime</th>
                <th>
                  <div className="stacked-header">
                    <div>Total</div>
                    <div>{selectedSource === 'BJS_NCVS' ? 'Victimizations' : 'Crime'}</div>
                  </div>
                </th>
                <th>
                  <div className="stacked-header">
                    <div>Previous</div>
                    <div>Total</div>
                  </div>
                </th>
                <th>
                  <div className="stacked-header">
                    <div>Change from</div>
                    <div>Previous Total</div>
                  </div>
                </th>
                <th>
                  <div className="stacked-header">
                    <div>% Change from</div>
                    <div>Previous Total</div>
                  </div>
                </th>
                {!isNation && (
                  <>
                    <th>
                      <div className="stacked-header">
                        <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                        <div>Rank</div>
                      </div>
                    </th>
                    <th>
                      <div className="stacked-header">
                        <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                        <div>Percentile</div>
                      </div>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {data.map(item => renderRow(item))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default LocationNameTrendsTable;